import { prepUrlForEnvironment } from '@sureapp/wl-toggle-common/dist/utils/url';

import { featureFlag } from '@@/auto/constants/featureFlags';
import { TConfigParams, TConfigInput, TConfig } from '@@/auto/interfaces';
import { ReactComponent as SuccessIcon } from '@@/common/assets/better/image/better-checkmark.svg';
import documentIcon from '@@/common/assets/better/image/better-document-icon.png';
import { ReactComponent as BetterLogoLight } from '@@/common/assets/better/image/better-logo--light.svg';
import { ReactComponent as BetterLogo } from '@@/common/assets/better/image/better-logo.svg';
import mfaImage from '@@/common/assets/better/image/better-mfa.png';
import builtOnSureLogo from '@@/common/assets/better/image/built-on-sure-logo.svg';
import { ReactComponent as TenantCarIcon } from '@@/common/assets/better/image/car.svg';
import { ReactComponent as CreditCardIcon } from '@@/common/assets/better/image/card.svg';
import { ReactComponent as BetterCaretIcon } from '@@/common/assets/better/image/caret.svg';
import { ReactComponent as PayByEscrowIcon } from '@@/common/assets/better/image/escrow-pay-icon.svg';
import { ReactComponent as TenantAlertIcon } from '@@/common/assets/better/image/green-alert.svg';
import { ReactComponent as HomeownersPolicyIcon } from '@@/common/assets/better/image/home-icon.svg';
import { ReactComponent as IdIcon } from '@@/common/assets/better/image/id_card_icon.svg';
import { ReactComponent as TenantInfoIconSolid } from '@@/common/assets/better/image/info-icon.svg';
import { ReactComponent as RentersPolicyIcon } from '@@/common/assets/better/image/renters-icon.svg';
import selfServiceHero from '@@/common/assets/better/image/self-service-hero.svg';
import { ReactComponent as ShieldIcon } from '@@/common/assets/better/image/shield.svg';
import { ReactComponent as RoadsideAssistanceIcon } from '@@/common/assets/better/image/tow_truck_icon.svg';
import BetterFooterHeader from '@@/common/components/Footer/headers/Better/FooterHeader';
import { TNavLinkWLConfigOptions } from '@@/common/components/NavBar/useNavLinks';

const isStandalone = process.env.REACT_APP_STANDALONE === 'true';
const localhostPort = isStandalone ? '8081' : '8080';

const mfaSuccessHost = prepUrlForEnvironment({
  url: process.env.REACT_APP_MFA_SUCCESS_REDIRECT_HOST,
});

function getConfig({
  isFeatureEnabled,
}: TConfigParams): DeepPartial<TConfigInput> {
  return {
    brazeConfig: {
      isBrazeEnabled: false,
    },
    cssFiles: ['better.css'],
    cmsSlugs: {
      authenticatedFooterSlug: 'better-authenticated-footer',
      copyrightSlug: 'better-copyright',
      footerSlug: 'better-footer',
      privacyPolicySlug: isFeatureEnabled(
        featureFlag.IS_PRIVACY_POLICY_V2_ENABLED
      )
        ? 'better-privacy-policy-v2'
        : 'better-privacy-policy',
      termsViewSlug: 'better-terms-and-conditions',
      billingViewSlug: 'better-billing',
    },
    errorPageConfig: {
      backgroundImgSrc: '',
    },
    featureFlags: {
      isAccountManagementEnabled: true,
      isAutoPurchaseFlowEnabled: true,
      isAutoQuoteLinkExchangeEnabled: false,
      isDirectToAutoQuoteEnabled: false,
      isLogoRedirectInAutoPurchaseFlowEnabled: false,
      isPrivacyAndTermsEnabled: true,
      isBillingEnabled: true,
      isQuoteAssistForTenantEnabled: false,
      isRentersQuoteCreationEnabled: false,
      isSelfServiceEnabled: true,
      isShowingSelfServicePromoCard: isFeatureEnabled(
        featureFlag.IS_AUTO_PROMOTION_ENABLED_FOR_HOMEOWNERS
      ),
    },
    privacyPolicyView: {
      showPersonalInfoTable: false,
      showDoNotSellMyPersonalInfo: false,
      showSharingPersonalInformation: false,
      showYourChoices: false,
      secondBlockCmsFields: [
        'How We Use Your Personal Information',
        'Your Rights to Access and Correction',
        'How We Protect Your Personal Information',
      ],
      thirdBlockCmsFields: [
        'Other Websites, Platforms and Applications',
        'Children and Minors',
        'Modifications to This Privacy Policy',
        'Do Not Sell My Personal Information/Notice of Right to Opt Out',
        'Signed',
      ],
    },
    footerConfig: {
      sureFooterLogoSrc: builtOnSureLogo,
      hasNavItems: false,
      header: <BetterFooterHeader />,
      getSocialLinks: () => [
        {
          href: 'https://twitter.com/betterdotcom',
          fabIcon: 'fa-twitter',
          label: 'Twitter',
        },
        {
          href: 'https://www.facebook.com/better/',
          fabIcon: 'fa-facebook-f',
          label: 'Facebook',
        },
        {
          href: 'https://www.instagram.com/betterdotcom/',
          fabIcon: 'fa-instagram',
          label: 'Instagram',
        },
        {
          href: 'https://www.linkedin.com/company/betterdotcom/',
          fabIcon: 'fa-linkedin-in',
          label: 'Linkedin',
        },
        {
          href: 'https://www.tiktok.com/@betterdotcom',
          fabIcon: 'fa-tiktok',
          label: 'TikTok',
        },
      ],
    },
    icons: (icons: TConfig['icons']) => ({
      ...icons,
      CaretIcon: BetterCaretIcon,
      SuccessIcon,
      TenantAlertIcon,
      AlertIcon: TenantAlertIcon,
      TenantCarIcon,
      TenantInfoIconSolid,
      TenantVehicleIcon: TenantCarIcon,
      CreditCardIcon,
      NewCreditCardIcon: CreditCardIcon,
      autoPolicyIcons: {
        AutoPolicyIcon: TenantCarIcon,
        idCardsIconSrc: documentIcon,
        policyIconSrc: documentIcon,
        IdIcon,
        RoadsideAssistanceIcon,
      },
      rentersPolicyIcons: {
        RentersPolicyIcon,
      },
      homeownersPolicyIcons: {
        HomeownersPolicyIcon,
        ShieldIcon,
        PayByEscrowIcon,
      },
      trustIcons: [
        'pf-visa',
        'pf-mastercard-alt',
        'pf-american-express-alt',
        'pf-discover',
      ],
    }),
    layoutSelfServiceConfig: {
      backgroundImgSrc: selfServiceHero,
    },
    localforageDbName: 'better',
    logos: {
      default: BetterLogo,
      light: BetterLogoLight,
      title: 'Better',
    },
    mfaConfig: {
      mfaImage,
    },
    navBarConfig: {
      getLinks: ({ isLoggedIn }: TNavLinkWLConfigOptions) => {
        const loggedInMenuLinks = [
          {
            to: '/my-account',
            title: 'My policies',
            dataTestId: 'policies',
          },
          {
            to: '/my-activity',
            title: 'My payments',
            dataTestId: 'payments',
          },
          {
            to: '/my-profile',
            title: 'My profile',
            dataTestId: 'profile',
          },
          {
            to: process.env.REACT_APP_CLAIMS_URL!,
            title: 'Claims',
            dataTestId: 'claims',
          },
          {
            to: process.env.REACT_APP_FAQ_URL!,
            title: 'FAQs',
            dataTestId: 'faqs',
          },
        ];

        const loggedInFooterLinks = [
          { to: process.env.REACT_APP_ABOUT_US_URL!, title: 'About Us' },
          { to: '/terms', title: 'Terms' },
          { to: '/privacy', title: 'Privacy' },
        ];

        return {
          main: isLoggedIn ? loggedInMenuLinks : [],
          footer: isLoggedIn ? loggedInFooterLinks : [],
          shouldHidePhoneLink: true,
          shouldHideSocialLinks: true,
          loginLogout: isLoggedIn ? { to: '/logout', title: 'Log out' } : null,
        };
      },
    },
    notifyConfig: {
      discountCopy: '',
    },
    partnerCode: 'toggle_better',
    redirectUrls: {
      indexRedirectUrl: {
        development: `http://localhost:${localhostPort}/homeowners/sign-up`,
        qa: 'https://better-dev.soda0014.com/homeowners/sign-up',
        sandbox: 'https://www.trygetbetterinsurance.com/homeowners/sign-up',
        production: 'https://www.get-betterinsurance.com/homeowners/sign-up',
      },
      mfaSuccessRedirectUrl: `${mfaSuccessHost}my-account`,
      isPartnerRedirectUrlEnabled: true,
      logoRedirectUrl: '/homeowners/sign-up',
    },
    stripeElStyles: {
      base: {
        fontFamily:
          "'BetterSans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
        fontSize: '14px',
        color: '#000',
        '::placeholder': {
          color: '#565d5a',
        },
      },
      fonts: [
        {
          family: 'BetterSans',
          // NOTE: this using relative URLs here causes issues with localhost (need https for non-mixed content), and e2ws, sandbox, and QA since they're password protected
          src: `url(https://sure-host.s3.amazonaws.com/static/farmers/GT-Haptik-Regular-2.ttf) format("truetype")`,
        },
      ],
      invalid: {
        color: '#000',
        iconColor: '#017848',
      },
    },
    tenantDisplayName: 'Better',
    tenantPhoneNumber: '',
    utmSourceTenantPhoneNumberMap: {},
    notFoundPageConfig: {
      ctaHref: '/homeowners/sign-up',
      isLightLogo: false,
    },
    loginConfig: {
      isLightLogo: false,
    },
    billingView: {
      header: 'Payment Authorization Terms and Conditions',
    },
  };
}

export default getConfig;
